// Replay the session bar by bar: a play button, a time-of-day slider and (optionally) speed.
import { useEffect, useState } from 'react';
import { PauseIcon, PlayIcon } from 'lucide-react';
import { NSE } from '@planb/core';
import { cn } from '@/lib/utils';
import { Slider } from '@/components/ui/slider';
import { Pills } from './market-ui';

export interface ReplayState { tt: number; follow: boolean; playing: boolean }

/** Bar index to show: the latest bar while following, else the replay position. */
export function useReplay(n: number, stepMs = 240) {
  const [follow, setFollow] = useState(true);
  const [t, setT] = useState(NSE.bars);
  const [playing, setPlaying] = useState(false);
  const [speed, setSpeed] = useState(2);
  const tt = follow ? n : Math.min(t, n);

  useEffect(() => {
    if (!playing) return;
    const id = setInterval(() => {
      setT((x) => {
        if (x + 1 >= n) { setPlaying(false); setFollow(true); return n; }
        return x + 1;
      });
    }, stepMs / speed);
    return () => clearInterval(id);
  }, [playing, speed, n, stepMs]);

  return {
    tt, n, playing, speed, setSpeed,
    toggle: () => {
      if (playing) { setPlaying(false); return; }
      setFollow(false);
      setT((x) => (x >= n ? 1 : Math.min(x, n)));
      setPlaying(true);
    },
    seek: (v: number) => { setPlaying(false); setT(v); setFollow(v >= n); },
    live: () => { setPlaying(false); setFollow(true); },
  };
}

export function ReplayBar({ r, time, showSpeed = true, compact = false }: {
  r: ReturnType<typeof useReplay>; time: string; showSpeed?: boolean;
  /** Icon-only button and a shorter slider, to sit in a row of pills. */
  compact?: boolean;
}) {
  return (
    <div className={cn('flex items-center rounded-full border bg-card pr-1.5 pl-1', compact ? 'h-7 gap-2' : 'h-9 gap-3')}>
      <button type="button" onClick={r.toggle} aria-label={r.playing ? 'Pause replay' : 'Replay the session'} title={r.playing ? 'Pause' : 'Replay the session'}
        className={cn('inline-flex items-center gap-1.5 rounded-full bg-brand-soft text-xs font-semibold text-brand-strong hover:bg-brand-soft/70', compact ? 'size-5 justify-center' : 'h-7 px-3')}>
        {r.playing ? <PauseIcon className="size-3.5" /> : <PlayIcon className="size-3.5" />}
        {!compact && (r.playing ? 'Pause' : 'Replay')}
      </button>
      <Slider className={compact ? 'w-28' : 'w-24 sm:w-40'} min={1} max={Math.max(1, r.n)} step={1} value={[r.tt || 1]} aria-label="Time of day"
        onValueChange={([v]) => r.seek(v)} />
      <output className={cn('num text-xs font-medium text-ink', compact ? 'min-w-10' : 'min-w-10 sm:min-w-22')}>{time}{r.tt === NSE.bars && !compact ? ' · close' : ''}</output>
      {showSpeed && (
        <Pills size="sm" label="Replay speed" value={String(r.speed)} onChange={(v) => r.setSpeed(Number(v))} className="max-sm:hidden"
          options={['1', '2', '4'].map((s) => ({ id: s, label: `${s}×` }))} />
      )}
    </div>
  );
}
